import { Container, Text, TextStyle } from 'pixi.js';

interface CommentaryMessage {
  text: Text;
  elapsed: number;
  duration: number;
}

const WEAPON_CRATE = [
  'A jewel from the heavens!',
  'A Gift from the Gods!',
  'Is it your birthday?',
  "What's in the box?",
  'First come, first served.',
  "It's raining weapons!",
  'A weapon crate!',
  'Party time!',
  'Christmas comes early!',
  "This ain't no packed lunch!",
  'Gift wrapped and ready to rock!',
  'I wonder what it contains?',
  'Can we tempt you with this?',
  'Someone likes you...',
  'Quick! A Weapon drop!',
  'Lucky, lucky, lucky!',
  'Cool things come in small packages.',
  'Collect and destroy!',
  "OMG IT'S A CRATE!!1! LOL",
];

const HEALTH_CRATE = [
  'This might save your bacon...',
  'Grab this if you want to stay in the battle!',
  'Red cross, worm happy.',
  'Grab it while you can.',
  'Collect or save, the choice is yours.',
  "This'll get you pink again!",
  'Attention injured worms, we have first aid!',
  "Dr. Blessing's cure it all.",
  "Dr. Valley's cure-all.",
  'Just what the Doctor Ordered!',
  'Certainly a healthy option...',
  'Extra health never hurt any worm...',
  'Very tempting...',
  'Energy bundle!',
  'Energy boost!',
];

const UTILITY_CRATE = [
  'Utility dispensed.',
  'Tools for fools.',
  'Special tools ahoy!',
  'DIY Supplement.',
  'Interesting...',
  'Collect or conquer?',
  'Equipment drop!',
  'A utility is as good as a weapon!',
  'Might be worth collecting?',
  'All great moves start with a utility!',
  'Coo! That looks useful!',
  'You might find this useful.',
  'Spanner manna.',
  'Top tools...',
];

const DEATH_HP = [
  '_ is dead.',
  '_ is no more.',
  '_ could take no more.',
  '_ has gone.',
  '_ is an ex-Worm.',
  '_ is history.',
  '_ shall be missed.',
  '_ bit the dust.',
  '_ takes a dirt nap.',
  "_ didn't make it.",
  '_ has kicked the bucket.',
  '_ has passed away.',
  '_ has ceased to be.',
  '_ met their demise.',
  '_ was rubbed out.',
  '_ has been wasted.',
  '_ has been terminated.',
  '_ was eradicated.',
  '_ deserved it.',
  '_ was asking for it.',
  "_ wasn't hard enough.",
  "_ is knocking on Heaven's door.",
  '_ joins the great Worm in the sky.',
  '_ has turned up his toes.',
  '_ is pushing up the daisies.',
  '_ is 6 feet under.',
  'Poor _',
  'Awww, poor _',
  'Shed a tear for poor _',
  'So long _',
  'It was nice knowing _',
  '_ waved goodbye.',
  'The dream is over for _',
  '_ has been erased!',
  '_ has been smoked!',
  '_ will see you all on the other side.',
  '_ has popped off to meet his maker!',
  "_ has launched his last 'zook!",
  '_ has gone to a better place.',
  'Come on in, _. Your time is up!',
  '_ has turned stiff.',
  '_ has been bumped off!',
  '_ sprouts wings.',
  'Follow the bright light, _',
];

const DEATH_DROWN = [
  '_ has drowned.',
  "_ couldn't swim.",
  '_ is sleeping with the fishies.',
  '_ is sleeping on the sea bed.',
  '_ lies on the bottom of the sea.',
  '_ is off in search of Atlantis.',
  'Water way to go, _',
  'Water surprise _ has gone!',
  '_ swam like a brick.',
  '_ swam like they fought!',
  '_ is more than wet behind the ears!',
  '_ has gone to marry a mermaid.',
  '_ went in the drink.',
  '_ took a nose dive.',
  '_ sank like the Titanic.',
  '_ is practising breathing water.',
  '_ has slipped off the hook.',
  '_ mistook water for land!',
];

const TEAM_ELIMINATED = [
  '_ is history.',
  '_ is out!',
  '_ is out of here!',
  '_ is missing in action.',
  "_ didn't make the grade.",
  '_ bit the bullet.',
  '_ bit the dust.',
  '_ fade away.',
  '_ has been eliminated!',
  "It's too late for _",
  'Have pity for _',
];

const TEAM_WINS = [
  'Victory for _!',
  'Congratulations to _!',
  'Well done _!',
  '_ wins the round!',
  '_ : Numero Uno!',
];

const SUDDEN_DEATH = [
  'SUDDEN DEATH MODE ON!',
  'Time out! SUDDEN DEATH ENGAGED!',
  "Make 'em Pay! SUDDEN DEATH!",
  "Careful out there... It's SUDDEN DEATH!",
  "Tactics out of the window... It's SUDDEN DEATH!",
  "It's time for... SUDDEN DEATH!",
  "Let's cut the chase huh? Sudden Death!",
  "Enough Already? It's time for Sudden Death!",
];

const SELF_DAMAGE = ['Oops!', 'Bummer!', 'Oi Nutter!', 'Stupid!', 'That had to hurt!', 'Own goal!'];

const FRIENDLY_FIRE = ['Traitor!', 'Watch your fire!', 'Friendly fire!', 'Wrong target!'];

const MISSED = ['Missed!', 'Oops!', 'So close!', 'Almost!', 'Better luck next time!'];

function pick<T>(arr: T[]): T {
  return arr[Math.floor(Math.random() * arr.length)];
}

function template(msg: string, name: string): string {
  return msg.replace(/_/g, name);
}

export class CommentaryManager {
  container: Container;
  private messages: CommentaryMessage[] = [];
  private style: TextStyle;
  private screenWidth: number = 1280;

  constructor() {
    this.container = new Container();
    this.style = new TextStyle({
      fontFamily: 'Fredoka, system-ui, Arial, sans-serif',
      fontSize: 18,
      fontWeight: '700',
      fill: 0xffffff,
      stroke: { color: 0x0a1426, width: 4 },
      align: 'center',
      dropShadow: { color: 0x000000, alpha: 0.5, blur: 2, angle: Math.PI / 2, distance: 2 },
    });
  }

  setScreenSize(width: number): void {
    this.screenWidth = width;
  }

  show(message: string, duration: number = 3.0): void {
    for (const existing of this.messages) {
      existing.text.destroy();
    }
    this.messages = [];

    const text = new Text({ text: message, style: this.style });
    text.anchor.set(0.5, 0);
    // Below the top-center wind indicator on desktop; on a narrow phone drop
    // it clear of the wide status / team-health panels + item row.
    const y = this.screenWidth < 700 ? 210 : 64;
    text.position.set(this.screenWidth / 2, y);
    text.alpha = 0;
    this.container.addChild(text);

    this.messages.push({
      text,
      elapsed: 0,
      duration,
    });
  }

  showWeaponCrateDrop(): void {
    this.show(pick(WEAPON_CRATE));
  }

  showHealthCrateDrop(): void {
    this.show(pick(HEALTH_CRATE));
  }

  showUtilityCrateDrop(): void {
    this.show(pick(UTILITY_CRATE));
  }

  showDeath(wormName: string): void {
    this.show(template(pick(DEATH_HP), wormName));
  }

  showDrown(wormName: string): void {
    this.show(template(pick(DEATH_DROWN), wormName));
  }

  showTeamEliminated(teamName: string): void {
    this.show(template(pick(TEAM_ELIMINATED), teamName));
  }

  showTeamWins(teamName: string): void {
    this.show(template(pick(TEAM_WINS), teamName));
  }

  showSuddenDeath(): void {
    this.show(pick(SUDDEN_DEATH), 4.0);
  }

  showSelfDamage(): void {
    this.show(pick(SELF_DAMAGE));
  }

  showFriendlyFire(): void {
    this.show(pick(FRIENDLY_FIRE));
  }

  showMissed(): void {
    this.show(pick(MISSED));
  }

  showTurnStart(wormName: string, teamName: string): void {
    // With one worm per team the names match; don't repeat it ("Boggy (Boggy)").
    this.show(wormName === teamName ? wormName : `${wormName} (${teamName})`);
  }

  update(deltaTime: number): void {
    for (let i = this.messages.length - 1; i >= 0; i--) {
      const msg = this.messages[i];
      msg.elapsed += deltaTime;

      const progress = msg.elapsed / msg.duration;

      if (progress >= 1) {
        msg.text.destroy();
        this.messages.splice(i, 1);
        continue;
      }

      const fadeInEnd = 0.1;
      const fadeOutStart = 0.8;

      if (progress < fadeInEnd) {
        msg.text.alpha = progress / fadeInEnd;
      } else if (progress > fadeOutStart) {
        msg.text.alpha = 1 - (progress - fadeOutStart) / (1 - fadeOutStart);
      } else {
        msg.text.alpha = 1;
      }
    }
  }

  destroy(): void {
    for (const msg of this.messages) {
      msg.text.destroy();
    }
    this.messages = [];
    this.container.destroy({ children: true });
  }
}
